import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress
} from '@mui/material'
import { addPrefix, type Prefix } from 'services'
import { useGlobalMessageHandler } from 'components'
import { formatErrorFieldMessages } from 'interfaces'
import { createEmptyPrefix } from 'helpers'

interface PrefixCreationDialogProps {
  open: boolean
  onClose: () => void
  onPrefixCreated: (prefix: Prefix) => void
}

export const PrefixCreationDialog: React.FC<PrefixCreationDialogProps> = ({
  open,
  onClose,
  onPrefixCreated
}) => {
  const [prefixValue, setPrefixValue] = useState('')
  const [loading, setLoading] = useState(false)
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()

  const handleClose = (): void => {
    setPrefixValue('')
    setLoading(false)
    clearMessages()
    onClose()
  }

  const handleSave = async (): Promise<void> => {
    if (prefixValue.trim() === '') {
      showErrorMessage('Prefix value is required')
      return
    }

    setLoading(true)
    clearMessages()

    try {
      const creationResponse = await addPrefix(createEmptyPrefix(prefixValue.trim()))
      if (!creationResponse.succeeded || creationResponse.result == null) {
        showErrorMessage(formatErrorFieldMessages(creationResponse.errors))
        return
      }
      showSuccessMessage('Prefix added successfully')
      onPrefixCreated(creationResponse.result)
      handleClose()
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      void handleSave()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Prefix</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Prefix Value"
          type="text"
          fullWidth
          variant="outlined"
          value={prefixValue}
          onChange={(e) => {
            setPrefixValue(e.target.value)
          }}
          onKeyPress={handleKeyPress}
          disabled={loading}
          placeholder="e.g., Dr., Prof., etc."
          helperText="Enter the prefix value (e.g., Dr., Prof., Hon.)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            void handleSave()
          }}
          variant="contained"
          disabled={loading || prefixValue.trim() === ''}
        >
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
